import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Button, Icon, Modal, hasUnsavedWork } from '@zagru/design-system';
import { describeError, sessionStore, useAuth, useSession, useT } from '@zagru/platform';
import { isMockMode } from '@/app/mockAuth';
import { ShellSkeleton } from '@/app/shell/ShellSkeleton';
import { useShellT } from '@/app/shell/i18n';
import { FailurePanel } from '@/components/feedback/states';

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Gate for the protected shell (ADR-0022).
 *
 * - While a callback or a silent restoration is in flight the shell skeleton is shown, so a reload or a deep link
 *   lands on the same page without passing through the sign-in screen.
 * - Anonymous users go to `/login?returnTo=` with the full location (path, query and hash).
 * - An expired session goes straight back to the sign-in form and returns to this screen; only when the screen holds
 *   unsaved work does it stay and ask first, so the work is not thrown away.
 * - `/me` is awaited so navigation and permissions are role-native from the first paint.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const { status, signedOut } = useAuth();
  const session = useSession();
  const location = useLocation();
  const t = useShellT();

  if (status === 'anonymous' || status === 'error') {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const params = new URLSearchParams();
    if (returnTo && returnTo !== '/') params.set('returnTo', returnTo);
    if (signedOut) params.set('signedOut', '1');
    const search = params.toString();
    return <Navigate to={`/login${search ? `?${search}` : ''}`} replace />;
  }
  if (status === 'idle' || status === 'authenticating') return <ShellSkeleton label={t('auth.restoringSession')} />;

  if (session.status === 'error') return <SessionFailure failure={session.failure} />;
  if (session.status !== 'ready') return <ShellSkeleton label={t('shell.loading')} />;

  return (
    <>
      {children}
      {status === 'expired' ? <SessionExpiredDialog /> : null}
    </>
  );
}

function SessionFailure({ failure }: { failure: unknown }) {
  const t = useT();
  const { logout } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const described = describeError(failure, t, { subject: t('auth.workspaceFailure.subject') });
  const retry = async () => {
    setRetrying(true);
    await sessionStore.getState().load();
    setRetrying(false);
  };
  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-0 p-6">
      <FailurePanel
        variant="page"
        title={t('auth.workspaceFailure.title')}
        description={
          described.kind === 'forbidden'
            ? t('auth.workspaceFailure.forbidden')
            : described.description
        }
        failure={described}
        onRetry={described.kind === 'forbidden' ? undefined : () => void retry()}
        retrying={retrying}
        secondaryAction={
          <Button variant="ghost" size="sm" onClick={() => void logout()}>
            {t('auth.sessionExpired.signOut')}
          </Button>
        }
      />
    </main>
  );
}

/**
 * The session could not be renewed. With nothing unsaved on screen the browser goes straight to the sign-in form and
 * comes back to this URL — no extra click (owner, 2026-09-25). With unsaved input the screen is kept and the operator
 * decides: an allowed overlay (D-016), a two-way decision over the screen it must not replace.
 */
function SessionExpiredDialog() {
  const t = useT();
  const { login, logout } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [unsaved] = useState(hasUnsavedWork);
  const [redirectFailed, setRedirectFailed] = useState(false);
  const redirecting = !isMockMode() && !unsaved && !redirectFailed;
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (isMockMode()) {
      void logout();
    } else if (!unsaved) {
      void login(returnTo).catch(() => setRedirectFailed(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  if (redirecting) return null;

  return (
    <Modal
      open
      onClose={() => undefined}
      closeOnEscape={false}
      closeOnOverlayClick={false}
      hideCloseButton
      size="sm"
      title={t('auth.sessionExpired.title')}
      description={t('auth.sessionExpired.description')}
      footer={
        <>
          <Button variant="ghost" onClick={() => void logout()}>
            {t('auth.sessionExpired.signOut')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            iconStart={<Icon name="log-in" size={15} />}
            onClick={() => {
              setBusy(true);
              void login(returnTo).catch(() => setBusy(false));
            }}
          >
            {t('auth.sessionExpired.signIn')}
          </Button>
        </>
      }
    />
  );
}
